import { createStyles } from "@mantine/core";
import React from "react";
import { tokens } from "@blurb/codex-react";
import type { IconSize } from "@blurb/codex-react/icons";
import { CopyButton } from "./CopyButton";

type CopyableRowOverflow = "wrap" | "none";

const useStyles = createStyles({
  row: {
    display: "flex",
    alignItems: "center",
    gap: tokens.spacing[1],
    minWidth: 0,
    // A flex item defaults to min-width:auto, which floors it at min-content — so a long
    // unbreakable id refuses to shrink and pushes the copy button out of view.
    "& > :first-child": {
      minWidth: 0,
    },
    '&[data-overflow="wrap"]': {
      justifyContent: "space-between",
      wordBreak: "break-word",
      "& > :first-child": { overflow: "hidden", textOverflow: "ellipsis" },
    },
  },
});

interface CopyableRowProps {
  value: string;
  size?: IconSize;
  overflow?: CopyableRowOverflow;
  copyTestId?: string;
  children: React.ReactNode;
}

/** Lays out arbitrary content next to a copy control wired to `value`. */
export const CopyableRow: React.FC<CopyableRowProps> = ({ value, size, overflow = "wrap", copyTestId, children }) => {
  const { classes } = useStyles();

  return (
    <div className={classes.row} data-overflow={overflow}>
      {children}
      <CopyButton value={value} size={size} testId={copyTestId} />
    </div>
  );
};
